import {
  fetchDataSamplesFromEditor,
  fetchDataSet,
  fetchTransformedSamplesFromEditor,
  initSchemaEditor,
  splitterSetLastTxPayload,
} from 'js/actions';
import { FORM_TYPE_CREATE_DATASET } from 'js/constants/formTypes';
import {
  SCHEMA_DISPLAY_LIVE_SAMPLES_DEFAULT,
  SCHEMA_DISPLAY_NUM_SAMPLES_DEFAULT,
} from 'js/constants/schemaEditor';
import { completeTxBuilderRecipeGroups } from 'js/lib/RecipeBuilderUtils';
import { has } from 'lodash';
import * as actionTypes from '../../constants/actionTypes';
import TransformsUtils from '../../lib/TransformsUtils';
import { updateFormData } from '../forms';
import { getFeatureFlagValue } from 'new/constants/featureFlags';

export const setRecipeGroups = (recipeGroups, isRecipeUnapplied = false, autoApply = false) => {
  return {
    type: actionTypes.RECIPE_BUILDER_SET_RECIPE_GROUPS,
    payload: {
      recipeGroups,
      isRecipeUnapplied,
      autoApply,
    },
  };
};

export const resetRecipeGroups = () => {
  return {
    type: actionTypes.RECIPE_BUILDER_RESET_RECIPE_GROUPS,
  };
};

export const setExpandedRecipeGroup = (index) => {
  return {
    type: actionTypes.RECIPE_BUILDER_SET_EXPANDED_RECIPE_GROUP,
    payload: {
      index,
    },
  };
};

export const setExpandedRecipe = (index) => {
  return {
    type: actionTypes.RECIPE_BUILDER_SET_EXPANDED_RECIPE,
    payload: {
      index,
    },
  };
};

export const recipeApplied = () => {
  return {
    type: actionTypes.RECIPE_BUILDER_RECIPE_APPLIED,
  };
};

export const selectRecipeBuilderAttr = (attrName, gridId) => {
  return {
    type: actionTypes.RECIPE_BUILDER_SELECT_ATTR,
    payload: {
      attrName,
      gridId,
    },
  };
};

export const selectAllRecipeBuilderAttrs = (schema, gridId) => {
  return {
    type: actionTypes.RECIPE_BUILDER_SELECT_ALL_ATTRS,
    payload: {
      schema,
      gridId,
    },
  };
};

export const deselectRecipeBuilderAttr = (attrName, gridId) => {
  return {
    type: actionTypes.RECIPE_BUILDER_DESELECT_ATTR,
    payload: {
      attrName,
      gridId,
    },
  };
};

export const deselectAllRecipeBuilderAttrs = () => {
  return {
    type: actionTypes.RECIPE_BUILDER_DESELECT_ALL_ATTRS,
  };
};

export const applySchemaSpec = (id) => {
  return {
    type: actionTypes.RECIPE_BUILDER_APPLY_SCHEMA_SPEC,
    payload: {
      id,
    },
  };
};

export const applyRecipe = (recipeGroups, inputSamples) => (dispatch, getState) => {
  const {
    entities: { transforms },
    recipeBuilder: { isLegacyMode },
  } = getState();

  // Get feature flag value for multi-block transforms
  const enableMultiBlock = getFeatureFlagValue('UI_ENABLE_MULTI_BLOCK_TRANSFORMS');

  const { txObject, saObject, reusableTransformId, outputValidations, parsingErrors } =
    TransformsUtils.getTxFromRecipe(recipeGroups, transforms, isLegacyMode, enableMultiBlock);

  dispatch(setRecipeBuilderParsingErrors(parsingErrors));

  return dispatch(
    fetchTransformedSamplesFromEditor(
      txObject,
      saObject,
      reusableTransformId,
      outputValidations,
      inputSamples,
    ),
  ).then((response) => {
    dispatch(recipeApplied());
  });
};

export const loadRecipeBuilderSamples =
  (
    dataSetId,
    numSamples,
    liveSamples,
    recipeGroups,
    shouldApplyTransform = false,
    isEditMode = false,
  ) =>
  (dispatch, getState) => {
    let txObject = null;
    let saObject = null;
    let reusableTransformId = 0;
    let outputValidations = [];

    if (shouldApplyTransform) {
      // Get feature flag value for multi-block transforms
      const enableMultiBlock = getFeatureFlagValue('UI_ENABLE_MULTI_BLOCK_TRANSFORMS');

      const parsed = TransformsUtils.getTxFromRecipe(
        recipeGroups,
        getState().entities.transforms,
        false, // isLegacyMode
        enableMultiBlock,
      );

      txObject = parsed.txObject;
      saObject = parsed.saObject;
      reusableTransformId = parsed.reusableTransformId;
      outputValidations = parsed.outputValidations;
    }

    return dispatch(
      fetchDataSamplesFromEditor(
        numSamples,
        liveSamples,
        dataSetId,
        txObject,
        saObject,
        reusableTransformId,
        outputValidations,
        !!isEditMode,
      ),
    );
  };

export const loadRecipeBuilderResources = (dataSetId, isEditMode) => (dispatch, getState) => {
  let fromDataSetId;
  let recipeGroups;

  return dispatch(fetchDataSet(dataSetId))
    .then((response) => {
      const {
        result,
        entities: { dataSets },
      } = response;
      const dataSet = dataSets[result];

      // in edit mode, extract recipe groups and fetch the parent dataset
      if (isEditMode) {
        const {
          entities: { transforms },
          forms,
        } = getState();
        const form = forms[FORM_TYPE_CREATE_DATASET];
        const formData = form ? form.data : null;
        const txSpec = dataSet.custom_config?.txSpec;
        recipeGroups =
          txSpec?.v === 2 ? txSpec.spec : TransformsUtils.getUpgradedTxSpec(dataSet, transforms);
        recipeGroups = completeTxBuilderRecipeGroups(recipeGroups, transforms);

        dispatch(setRecipeGroups(recipeGroups));
        dispatch(setExpandedRecipeGroup(recipeGroups.length - 1));
        dispatch(setRecipeBuilderAsync(dataSet.runtime_config?.['node.tag'] === 'async'));

        if (!has(formData, 'addedDatasetId')) {
          dispatch(
            updateFormData(FORM_TYPE_CREATE_DATASET, {
              addedDatasetId: dataSetId,
            }),
          );
        }

        fromDataSetId = dataSet.parent_data_sets[0].id;

        if (dataSet.node_type === 'splitter') {
          dispatch(splitterSetLastTxPayload(dataSet.transform));
        }

        return dispatch(fetchDataSet(fromDataSetId));
      }

      fromDataSetId = dataSetId;
    })
    .then(() => {
      const { dataSetSchemas } = getState();
      const inputSchema = dataSetSchemas[fromDataSetId]?.out;
      const outputSchema = isEditMode ? dataSetSchemas[dataSetId]?.out : null;

      return dispatch(initSchemaEditor(fromDataSetId, inputSchema, outputSchema));
    })
    .then(() =>
      // fetch samples of fromDataSetId (LHS). in edit mode, apply recipe.
      dispatch(
        loadRecipeBuilderSamples(
          fromDataSetId,
          SCHEMA_DISPLAY_NUM_SAMPLES_DEFAULT,
          SCHEMA_DISPLAY_LIVE_SAMPLES_DEFAULT,
          recipeGroups,
          isEditMode,
          isEditMode,
        ),
      ),
    );
};

export const updateDestinationDraft = (destinationDraft) => {
  return {
    type: actionTypes.UPDATE_DESTINATION_DRAFT,
    payload: {
      destinationDraft,
    },
  };
};

export const accumulateSchemaToDestinationDraft = (data) => {
  return {
    type: actionTypes.ACCUMULATE_SCHEMA,
    payload: {
      data,
    },
  };
};

export const setRecipeBuilderAsync = (isAsync = false) => {
  return {
    type: actionTypes.RECIPE_BUILDER_SET_IS_ASYNC,
    payload: {
      isAsync,
    },
  };
};

export const setRecipeBuilderParsingErrors = (parsingErrors) => {
  return {
    type: actionTypes.RECIPE_BUILDER_SET_PARSING_ERRORS,
    payload: {
      parsingErrors,
    },
  };
};

export const updateNexsetSampleDraft = (nexsetSampleDraft) => {
  return {
    type: actionTypes.UPDATE_NEXSET_SAMPLE_DRAFT,
    payload: {
      nexsetSampleDraft,
    },
  };
};

export const accumulateSchemaToNexsetSampleDraft = (data) => {
  return {
    type: actionTypes.ACCUMULATE_SCHEMA_FOR_NEXSET_SAMPLE,
    payload: {
      data,
    },
  };
};

export const clearNexsetSampleDraft = () => {
  return {
    type: actionTypes.CLEAR_NEXSET_SAMPLE_DRAFT,
  };
};

export const setLegacyMode = (isLegacyMode) => {
  return {
    type: actionTypes.RECIPE_BUILDER_SET_LEGACY_MODE,
    payload: {
      isLegacyMode,
    },
  };
};
